import { useEffect, useRef } from "react";
import api from "../api/axios";
import { getNativePlatform, isNativeApp } from "../native/runtime";

const INSTALLATION_ID_KEY = "vg_native_installation_id_v1";
const REPORT_INTERVAL_MS = 24 * 60 * 60 * 1000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function createInstallationId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  if (!window.crypto?.getRandomValues) return null;
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getNativeInstallationId() {
  try {
    const existing = window.localStorage.getItem(INSTALLATION_ID_KEY);
    if (existing && UUID_RE.test(existing)) return existing;
    const created = createInstallationId();
    if (!created) return null;
    window.localStorage.setItem(INSTALLATION_ID_KEY, created);
    return created;
  } catch {
    return null;
  }
}

export default function useNativeBuildTelemetry(userId) {
  const lastSuccessRef = useRef(null);

  useEffect(() => {
    if (!userId || !isNativeApp()) return undefined;
    const platform = getNativePlatform();
    if (platform !== "ios" && platform !== "android") return undefined;

    let active = true;
    let inFlight = false;
    const report = async () => {
      if (!active || inFlight) return;
      const installationId = getNativeInstallationId();
      if (!installationId) return;
      inFlight = true;
      try {
        const { App } = await import("@capacitor/app");
        const info = await App.getInfo();
        if (!active) return;
        const buildNumber = Number(info?.build);
        const appVersion = String(info?.version || "").trim();
        if (!Number.isInteger(buildNumber) || buildNumber < 1 || !appVersion) return;
        const signature = `${userId}:${platform}:${appVersion}:${buildNumber}`;
        const previous = lastSuccessRef.current;
        if (previous?.signature === signature && Date.now() - previous.at < REPORT_INTERVAL_MS) return;
        await api.post("/notifications/app-build/", {
          installation_id: installationId,
          platform,
          app_version: appVersion,
          build_number: buildNumber,
        }, { meta: { suppressGlobalError: true, suppressUnauthorizedRedirect: true } });
        if (active) lastSuccessRef.current = { signature, at: Date.now() };
      } catch (error) {
        // Try again on resume or the next app launch after a transient outage.
        console.warn("Native build report unavailable", error);
      } finally {
        inFlight = false;
      }
    };

    void report();
    window.addEventListener("vg:native-resume", report);
    return () => {
      active = false;
      window.removeEventListener("vg:native-resume", report);
    };
  }, [userId]);
}
